import type {
  CoinFinish,
  CoinVariantDefinition,
  CoinVariantId,
  CoinValue,
} from './types';

export const DEFAULT_COIN_VARIANT: CoinVariantId = 'gold-50';

export const COIN_VARIANTS: Readonly<Record<CoinVariantId, CoinVariantDefinition>> = Object.freeze({
  'copper-1': Object.freeze({
    id: 'copper-1',
    value: 1,
    label: '1',
    finish: 'copper',
    rarity: 'common',
    radius: 0.36,
    thickness: 0.074,
    mass: 0.018,
    friction: 0.58,
    restitution: 0.1,
    linearDamping: 0.12,
    angularDamping: 0.34,
  }),
  'brass-5': Object.freeze({
    id: 'brass-5',
    value: 5,
    label: '5',
    finish: 'brass',
    rarity: 'common',
    radius: 0.375,
    thickness: 0.078,
    mass: 0.021,
    friction: 0.56,
    restitution: 0.1,
    linearDamping: 0.12,
    angularDamping: 0.35,
  }),
  'nickel-10': Object.freeze({
    id: 'nickel-10',
    value: 10,
    label: '10',
    finish: 'nickel',
    rarity: 'uncommon',
    radius: 0.39,
    thickness: 0.08,
    mass: 0.024,
    friction: 0.54,
    restitution: 0.11,
    linearDamping: 0.13,
    angularDamping: 0.36,
  }),
  'silver-25': Object.freeze({
    id: 'silver-25',
    value: 25,
    label: '25',
    finish: 'silver',
    rarity: 'rare',
    radius: 0.405,
    thickness: 0.084,
    mass: 0.028,
    friction: 0.52,
    restitution: 0.11,
    linearDamping: 0.13,
    angularDamping: 0.37,
  }),
  'gold-50': Object.freeze({
    id: 'gold-50',
    value: 50,
    label: '50',
    finish: 'gold',
    rarity: 'epic',
    radius: 0.425,
    thickness: 0.09,
    mass: 0.033,
    friction: 0.5,
    restitution: 0.12,
    linearDamping: 0.14,
    angularDamping: 0.39,
  }),
  'platinum-100': Object.freeze({
    id: 'platinum-100',
    value: 100,
    label: '100',
    finish: 'platinum',
    rarity: 'legendary',
    radius: 0.45,
    thickness: 0.096,
    mass: 0.039,
    friction: 0.48,
    restitution: 0.13,
    linearDamping: 0.14,
    angularDamping: 0.4,
  }),
});

export const COIN_VARIANT_IDS = Object.freeze(Object.keys(COIN_VARIANTS) as CoinVariantId[]);

const VALUE_TO_VARIANT: Readonly<Record<CoinValue, CoinVariantId>> = Object.freeze({
  1: 'copper-1',
  5: 'brass-5',
  10: 'nickel-10',
  25: 'silver-25',
  50: 'gold-50',
  100: 'platinum-100',
});

export function getCoinVariant(id: CoinVariantId = DEFAULT_COIN_VARIANT): CoinVariantDefinition {
  return COIN_VARIANTS[id];
}

export function getCoinVariantForValue(value: CoinValue): CoinVariantDefinition {
  return COIN_VARIANTS[VALUE_TO_VARIANT[value]];
}

export interface CoinFinishPalette {
  readonly face: number;
  readonly rim: number;
  readonly relief: number;
  readonly roughness: number;
}

export const COIN_FINISH_PALETTES: Readonly<Record<CoinFinish, CoinFinishPalette>> = Object.freeze({
  copper: Object.freeze({ face: 0xb9683f, rim: 0x6f3825, relief: 0xf0a16f, roughness: 0.3 }),
  brass: Object.freeze({ face: 0xd2a238, rim: 0x795b20, relief: 0xffdf83, roughness: 0.27 }),
  nickel: Object.freeze({ face: 0xb5b2a8, rim: 0x62625f, relief: 0xf0eee7, roughness: 0.25 }),
  silver: Object.freeze({ face: 0xcbd6df, rim: 0x6a7884, relief: 0xffffff, roughness: 0.22 }),
  gold: Object.freeze({ face: 0xe0aa35, rim: 0x815613, relief: 0xffe990, roughness: 0.2 }),
  platinum: Object.freeze({ face: 0xd7e6ee, rim: 0x6f8795, relief: 0xe0fbff, roughness: 0.18 }),
});
